const DEFAULT_MAX_ENTRIES_PER_APP = 500;

export type LogLevel = "debug" | "info" | "warn" | "error";

export type LogContext = Record<string, unknown>;

export type LogEntry = {
    id: string;
    app: string;
    location?: string;
    level: LogLevel;
    content: string;
    timestamp: string;
    context?: LogContext;
};

export type LogQuery = {
    app?: string;
    location?: string;
    level?: LogLevel;
    since?: Date | string;
};

export type LoggerEnabled = boolean | (() => boolean);

export type LoggerPredicate = (level: LogLevel, content: string, context?: LogContext | Error) => boolean;

export type LoggerConsoleWriter = (entry: LogEntry, level: LogLevel) => void;

export type LoggerOptions = {
    enabled?: LoggerEnabled;
    shouldLog?: LoggerPredicate;
    consoleWriter?: LoggerConsoleWriter | null;
};

// Codex Added: 2025-09-18 ログビューア自動更新向けのイベント種別
export type LogStoreEvent =
    | { type: 'added'; entry: LogEntry }
    | { type: 'cleared'; app?: string; location?: string | null };

type LogListener = (event: LogStoreEvent) => void;

type LoggerIdentity = {
    app: string;
    location?: string;
};

const sanitizeAppName = (value: string) => value.trim() || "unknown";

const sanitizeLocation = (value?: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    return trimmed.length === 0 ? undefined : trimmed;
};

const createLoggerKey = ({ app, location }: LoggerIdentity) => `${app.toLowerCase()}::${(location ?? "").toLowerCase()}`;

const extractIdentity = (rawAppName: string, explicitLocation?: string): LoggerIdentity => {
    const trimmedName = (rawAppName ?? "").trim();
    const colonIndex = trimmedName.indexOf(":");
    const baseApp = colonIndex === -1 ? trimmedName : trimmedName.slice(0, colonIndex);
    const derivedLocation = colonIndex === -1 ? undefined : trimmedName.slice(colonIndex + 1);
    const location = explicitLocation !== undefined ? explicitLocation : derivedLocation;
    return {
        app: sanitizeAppName(baseApp),
        location: sanitizeLocation(location),
    };
};

const createId = (() => {
    let counter = 0;
    return () => {
        counter = (counter + 1) % Number.MAX_SAFE_INTEGER;
        return `${Date.now().toString(36)}-${counter.toString(36)}`;
    };
})();

const normalizeSince = (since?: Date | string) => {
    if (!since) return undefined;
    if (since instanceof Date) return since.getTime();
    const parsed = Date.parse(since);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const cloneEntry = (entry: LogEntry): LogEntry => ({ ...entry, context: entry.context ? { ...entry.context } : undefined });

const normalizeContext = (context?: LogContext | Error): LogContext | undefined => {
    if (!context) return undefined;
    if (context instanceof Error) {
        return {
            type: context.name,
            message: context.message,
            stack: context.stack,
        };
    }
    return { ...context };
};

export class LogStore {
    private readonly entries = new Map<string, LogEntry[]>();
    private readonly listeners = new Set<LogListener>();

    constructor(private readonly maxEntriesPerApp: number = DEFAULT_MAX_ENTRIES_PER_APP) {}

    add(entry: LogEntry): LogEntry {
        const list = this.entries.get(entry.app) ?? [];
        list.push(entry);
        if (list.length > this.maxEntriesPerApp) {
            list.splice(0, list.length - this.maxEntriesPerApp);
        }
        this.entries.set(entry.app, list);
        // Codex Added: 2025-09-18 追加イベントを通知
        this.notify({ type: 'added', entry });
        return entry;
    }

    getEntries(query?: LogQuery): LogEntry[] {
        const since = normalizeSince(query?.since);
        const baseEntries = query?.app
            ? [...(this.entries.get(query.app) ?? [])]
            : Array.from(this.entries.values()).flatMap((items) => items);

        return baseEntries
            .filter((entry) => {
                if (query?.level && entry.level !== query.level) return false;
                if (query?.location && entry.location !== query.location) return false;
                if (since !== undefined && Date.parse(entry.timestamp) < since) return false;
                return true;
            })
            .map((entry) => cloneEntry(entry));
    }

    clear(app?: string, location?: string | null) {
        if (!app) {
            this.entries.clear();
            // Codex Added: 2025-09-18 全件削除イベントを通知
            this.notify({ type: 'cleared' });
            return;
        }
        if (location === undefined) {
            this.entries.delete(app);
            // Codex Added: 2025-09-18 アプリ単位削除イベントを通知
            this.notify({ type: 'cleared', app });
            return;
        }
        const list = this.entries.get(app);
        if (!list) return;
        const filtered = list.filter((entry) => (entry.location ?? null) !== location);
        if (filtered.length === 0) {
            this.entries.delete(app);
            // Codex Added: 2025-09-18 ロケーション削除後にアプリごと空になった場合も通知
            this.notify({ type: 'cleared', app, location });
        } else {
            this.entries.set(app, filtered);
            // Codex Added: 2025-09-18 ロケーション削除イベントを通知
            this.notify({ type: 'cleared', app, location });
        }
    }

    subscribe(listener: LogListener): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    // Codex Added: 2025-09-18 ログストアの変更をリスナーへ伝播
    private notify(event: LogStoreEvent) {
        this.listeners.forEach((listener) => listener(event));
    }
}

const sharedStore = new LogStore();
const loggers = new Map<string, Logger>();

const levelToConsoleMethod: Record<LogLevel, keyof Console> = {
    debug: "debug",
    info: "info",
    warn: "warn",
    error: "error",
};

const fallbackConsole: Console = {
    ...console,
    debug: console.debug ?? console.log,
    info: console.info ?? console.log,
    warn: console.warn ?? console.log,
    error: console.error ?? console.log,
} as Console;

export class Logger {
    private readonly appName: string;
    private readonly location?: string;
    private readonly store: LogStore;
    private readonly clock: () => Date;
    private options: LoggerOptions;

    constructor(
        identity: LoggerIdentity,
        store: LogStore = sharedStore,
        clock: () => Date = () => new Date(),
        options: LoggerOptions = {}
    ) {
        this.appName = identity.app;
        this.location = identity.location;
        this.store = store;
        this.clock = clock;
        this.options = { ...options };
    }

    configure(options: LoggerOptions = {}) {
        this.options = { ...this.options, ...options };
    }

    private isEnabled(level: LogLevel, content: string, context?: LogContext | Error): boolean {
        const { enabled, shouldLog } = this.options;
        if (typeof enabled === "boolean") {
            if (!enabled) return false;
        } else if (typeof enabled === "function") {
            try {
                if (!enabled()) return false;
            } catch {
                return false;
            }
        }
        if (!shouldLog) return true;
        try {
            return shouldLog(level, content, context);
        } catch {
            return false;
        }
    }

    private writeConsole(entry: LogEntry, level: LogLevel) {
        const { consoleWriter } = this.options;
        if (consoleWriter === null) return;
        if (consoleWriter) {
            try {
                consoleWriter(entry, level);
            } catch {
                // ignore console writer failure
            }
            return;
        }
        const method = levelToConsoleMethod[level];
        const target = (fallbackConsole[method] ?? console.log) as (...args: unknown[]) => void;
        target.call(console, entry.content, entry.context);
    }

    log(level: LogLevel, content: string, context?: LogContext | Error): LogEntry {
        const entry: LogEntry = {
            id: createId(),
            app: this.appName,
            location: this.location,
            level,
            content,
            timestamp: this.clock().toISOString(),
            context: normalizeContext(context),
        };
        if (!this.isEnabled(level, content, context)) {
            return entry;
        }
        this.writeConsole(entry, level);
        return this.store.add(entry);
    }

    debug(content: string, context?: LogContext | Error) {
        return this.log("debug", content, context);
    }

    info(content: string, context?: LogContext | Error) {
        return this.log("info", content, context);
    }

    warn(content: string, context?: LogContext | Error) {
        return this.log("warn", content, context);
    }

    error(content: string, context?: LogContext | Error) {
        return this.log("error", content, context);
    }

    getEntries(query?: Omit<LogQuery, "app">) {
        return this.store.getEntries({ ...query, app: this.appName, location: this.location });
    }

    clear() {
        this.store.clear(this.appName, this.location ?? null);
    }

    subscribe(listener: LogListener) {
        return this.store.subscribe(listener);
    }
}

export const getSharedLogStore = () => sharedStore;

export function getLogger(appName: string): Logger;
export function getLogger(appName: string, options: LoggerOptions): Logger;
export function getLogger(appName: string, location: string): Logger;
export function getLogger(appName: string, location: string, options: LoggerOptions): Logger;
export function getLogger(
    appName: string,
    locationOrOptions?: string | LoggerOptions,
    maybeOptions?: LoggerOptions
): Logger {
    let location: string | undefined;
    let options: LoggerOptions | undefined;

    if (typeof locationOrOptions === "string") {
        location = locationOrOptions;
        options = maybeOptions;
    } else {
        options = locationOrOptions;
    }

    const identity = extractIdentity(appName, location);
    const key = createLoggerKey(identity);
    const existing = loggers.get(key);
    if (existing) {
        if (options) {
            existing.configure(options);
        }
        return existing;
    }
    const logger = new Logger(identity, sharedStore, () => new Date(), options ?? {});
    loggers.set(key, logger);
    return logger;
}

export const resetLoggerRegistry = () => {
    loggers.clear();
};
